import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Container,
  Heading,
  SimpleGrid,
  Card,
  CardBody,
  CardHeader,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  StatArrow,
  Text,
  VStack,
  HStack,
  Badge,
  Progress,
  Button,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Avatar,
  AvatarGroup,
  Flex,
  Grid,
  GridItem,
  useColorMode,
  useColorModeValue,
  IconButton,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Input,
  Select,
  FormControl,
  FormLabel,
  Switch,
  Divider,
  Alert,
  AlertIcon,
  Spinner,
  Tooltip,
  CircularProgress,
  CircularProgressLabel,
  useToast,
  Icon,
  useBreakpointValue,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
} from "@chakra-ui/react";
import {
  FiHome,
  FiUsers,
  FiBarChart2,
  FiTrendingUp,
  FiTrendingDown,
  FiActivity,
  FiTarget,
  FiDollarSign,
  FiClock,
  FiCalendar,
  FiSettings,
  FiRefreshCw,
  FiDownload,
  FiEye,
  FiMoreVertical,
  FiSun,
  FiMoon,
  FiAlertTriangle,
  FiCheckCircle,
  FiInfo,
  FiFilter,
  FiSearch,
  FiPieChart,
  FiPackage,
  FiLayers,
  FiZap,
} from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import toast from "react-hot-toast";
import api from "../../services/api";
import ProjectDashboardTable from './ProjectDashboardTable';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  AreaChart,
  Area,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
  ComposedChart,
} from "recharts";

const CompanyDashboard = () => {
  // Dark theme support
  const { colorMode, toggleColorMode } = useColorMode();

  // Enhanced glassmorphic color modes
  const bgPrimary = useColorModeValue(
    "rgba(255, 255, 255, 0.9)",
    "rgba(26, 32, 44, 0.9)"
  );
  const bgSecondary = useColorModeValue(
    "rgba(247, 250, 252, 0.8)",
    "rgba(45, 55, 72, 0.8)"
  );
  const bgTertiary = useColorModeValue(
    "rgba(237, 242, 247, 0.7)",
    "rgba(74, 85, 104, 0.7)"
  );
  const textPrimary = useColorModeValue("gray.800", "white");
  const textSecondary = useColorModeValue("gray.600", "gray.300");
  const textMuted = useColorModeValue("gray.500", "gray.400");
  const borderColor = useColorModeValue(
    "rgba(226, 232, 240, 0.5)",
    "rgba(74, 85, 104, 0.5)"
  );
  const hoverBg = useColorModeValue(
    "rgba(247, 250, 252, 0.9)",
    "rgba(45, 55, 72, 0.9)"
  );
  const shadowColor = useColorModeValue(
    "rgba(0, 0, 0, 0.1)",
    "rgba(0, 0, 0, 0.4)"
  );

  // Premium gradient backgrounds with glassmorphism
  const gradientBg = useColorModeValue(
    "linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)",
    "linear-gradient(135deg, rgba(15, 15, 35, 0.95) 0%, rgba(26, 26, 46, 0.95) 50%, rgba(22, 33, 62, 0.95) 100%)"
  );

  const cardGradient = useColorModeValue(
    "linear-gradient(145deg, rgba(255, 255, 255, 0.95) 0%, rgba(247, 250, 252, 0.9) 100%)",
    "linear-gradient(145deg, rgba(26, 32, 44, 0.95) 0%, rgba(45, 55, 72, 0.9) 100%)"
  );

  // Glassmorphic card styling
  const glassCard = {
    background: useColorModeValue(
      "rgba(255, 255, 255, 0.7)",
      "rgba(26, 32, 44, 0.7)"
    ),
    backdropFilter: "blur(16px) saturate(180%)",
    WebkitBackdropFilter: "blur(16px) saturate(180%)",
    border: `1px solid ${useColorModeValue(
      "rgba(209, 213, 219, 0.3)",
      "rgba(74, 85, 104, 0.3)"
    )}`,
    boxShadow: useColorModeValue(
      "0 8px 32px 0 rgba(31, 38, 135, 0.15)",
      "0 8px 32px 0 rgba(0, 0, 0, 0.5)"
    ),
  };

  // Premium color palette for charts
  const chartColors = {
    primary: ["#667eea", "#764ba2", "#f093fb", "#4facfe"],
    success: ["#10b981", "#34d399", "#6ee7b7", "#a7f3d0"],
    warning: ["#f59e0b", "#fbbf24", "#fcd34d", "#fde68a"],
    danger: ["#ef4444", "#f87171", "#fca5a5", "#fecaca"],
    info: ["#3b82f6", "#60a5fa", "#93c5fd", "#bfdbfe"],
    purple: ["#8b5cf6", "#a78bfa", "#c4b5fd", "#ddd6fe"],
  };

  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();

  // State Management
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("all");
  const [selectedPriority, setSelectedPriority] = useState("all");
  const [refreshing, setRefreshing] = useState(false);
  const [selectedTabIndex, setSelectedTabIndex] = useState(0);

  // Responsive columns
  const columns = useBreakpointValue({ base: 1, md: 2, lg: 3, xl: 5 });

  // Fetch dashboard data using API service
  const fetchDashboardData = async () => {
    try {
      setError(null);
      setLoading(true);

      console.log("Fetching dashboard data using API service...");
      const response = await api.get("/company/dashboard-stats");

      console.log("Dashboard API response:", response);

      if (response.data.success) {
        setDashboardData(response.data.data);
        // toast.success('Dashboard loaded successfully!');
      } else {
        throw new Error(
          response.data.message || "Failed to fetch dashboard data"
        );
      }
    } catch (err) {
      console.error("Dashboard fetch error:", err);
      setError(
        err.response?.data?.message ||
        err.message ||
        "Failed to fetch dashboard data"
      );

      if (err.response?.status === 401) {
        console.log("Authentication failed, redirecting to login");
        navigate("/login");
      }
    } finally {
      setLoading(false);
    }
  };

  // Refresh data
  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchDashboardData();
    setRefreshing(false);
  };

  // Auth handling
  useEffect(() => {
    console.log("CompanyDashboard useEffect triggered");

    if (authLoading) {
      console.log("Auth is still loading, waiting...");
      return;
    }

    if (!user?.id) {
      console.log("No user found, redirecting to login");
      navigate("/login");
      return;
    }

    console.log("User authenticated, fetching dashboard data...");
    fetchDashboardData();
  }, [authLoading, user, navigate]);

  // Enhanced utility functions matching BudgetSummary
  const formatNumber = (num) => {
    return new Intl.NumberFormat("en-US", {
      minimumFractionDigits: 1,
      maximumFractionDigits: 2,
    }).format(num || 0);
  };

  const formatCurrency = (amount, currency = "AED") => {
    const projectCurrency = currency || "AED";
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: projectCurrency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount || 0);
  };
  const chartTextColor = useColorModeValue("#e2e8f0", "#e2e8f0");
  const chartGridColor = useColorModeValue(
    "rgba(203, 213, 224, 0.5)",
    "rgba(71, 85, 105, 0.5)"
  );

  // NEW: Prepare Project Resource Analysis data
  const prepareProjectResourceData = () => {
    if (!dashboardData?.projects) return [];

    return dashboardData.projects.slice(0, 8).map((project) => ({
      name:
        project.name.length > 15
          ? project.name.substring(0, 15) + "..."
          : project.name,
      manHours: project.totalBudgetedHours || 0,
      consumedHours: project.totalConsumedHours || 0,
      budget: project.totalBudgetedCost,
      consumed: project.totalConsumedCost,
      earnedHours: project.totalEarnedManhours || 0,
      teamSize: project.teamMembers?.length || 0,
    }));
  };

  const formatDetailedCurrency = (amount, currency = "AED") => {
    const projectCurrency = currency || "AED";
    if (amount >= 1000000) {
      return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: projectCurrency,
        minimumFractionDigits: 1,
        maximumFractionDigits: 1,
        notation: "compact",
        compactDisplay: "short",
      }).format(amount);
    }
    return formatCurrency(amount, projectCurrency);
  };

  const formatCompactCurrency = (amount, currency = "AED") => {
    if (amount >= 1000000) {
      return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: currency,
        notation: "compact",
        compactDisplay: "short",
      }).format(amount);
    }
    return formatCurrency(amount, currency);
  };

  const getProgressColor = (percentage) => {
    if (percentage >= 80) return "green";
    if (percentage >= 60) return "yellow";
    return "red";
  };

  const getCPIColor = (cpi) => {
    if (cpi >= 1.0) return "green";
    if (cpi >= 0.9) return "yellow";
    return "red";
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "active":
        return "green";
      case "planning":
        return "yellow";
      case "completed":
        return "blue";
      case "on-hold":
        return "orange";
      case "cancelled":
        return "red";
      default:
        return "gray";
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case "high":
        return "red";
      case "medium":
        return "yellow";
      case "low":
        return "green";
      default:
        return "gray";
    }
  };

  const getHealthColor = (health) => {
    switch (health) {
      case "ahead":
      case "under":
      case "excellent":
      case "low":
        return "green";
      case "on-track":
      case "good":
      case "medium":
        return "yellow";
      case "behind":
      case "over":
      case "fair":
      case "high":
        return "orange";
      case "poor":
      case "critical":
        return "red";
      default:
        return "gray";
    }
  };
  // Inside your CompanyDashboard component, add these functions before the return statement
  // (Place them near where you have other helper functions like prepareBudgetData)

  const prepareManhoursData = () => {
    if (!projects || projects.length === 0) return [];

    return projects.map((project) => {
      console.log("Project Data:", project);
      const earnedHours = (project.totalEarnedManhours || 0);

      return {
        name: project.name.length > 20
          ? project.name.substring(0, 20) + "..."
          : project.name,
        budgetedHours: project.totalBudgetedHours || 0,
        consumedHours: project.totalConsumedHours || 0,
        earnedHours: earnedHours,
        cpi: project.projectCPI || 1.00,
        // Include cost data for tooltip
        budgetedCost: project.totalBudgetedCost || 0,
        consumedCost: project.totalConsumedCost || 0,
        currency: project.currency || "AED",
      };
    });
  };

  const CustomManhoursTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;

      return (
        <Box
          bg={bgPrimary}
          p={4}
          borderRadius="lg"
          boxShadow="xl"
          border="1px"
          borderColor={borderColor}
          minW="250px"
        >
          <Text fontWeight="bold" mb={2} color={textPrimary}>
            {label}
          </Text>
          <VStack align="stretch" spacing={2}>
            <HStack justify="space-between">
              <Text fontSize="sm" color={textSecondary}>
                Budgeted Manhours:
              </Text>
              <Text fontSize="sm" fontWeight="bold" color="#667eea">
                {formatNumber(data.budgetedHours)} hrs
              </Text>
            </HStack>
            <HStack justify="space-between">
              <Text fontSize="sm" color={textSecondary}>
                Consumed Manhours:
              </Text>
              <Text fontSize="sm" fontWeight="bold" color="#ef4444">
                {formatNumber(data.consumedHours)} hrs
              </Text>
            </HStack>
            <HStack justify="space-between">
              <Text fontSize="sm" color={textSecondary}>
                Earned Manhours:
              </Text>
              <Text fontSize="sm" fontWeight="bold" color="#10b981">
                {formatNumber(data.earnedHours)} hrs
              </Text>
            </HStack>
            {/* <Divider />
            <HStack justify="space-between">
              <Text fontSize="sm" color={textSecondary}>
                CPI:
              </Text>
              <Badge
                colorScheme={
                  data.cpi >= 1.1 ? "green" :
                    data.cpi >= 0.9 ? "blue" :
                      data.cpi >= 0.8 ? "yellow" : "red"
                }
              >
                {data.cpi.toFixed(2)}
              </Badge>
            </HStack> */}
            <Divider />
            <Text fontSize="xs" color={textMuted} fontWeight="medium" mt={1}>
              Cost Details:
            </Text>
            <HStack justify="space-between">
              <Text fontSize="xs" color={textSecondary}>
                Budgeted:
              </Text>
              <Text fontSize="xs" color={textSecondary}>
                {formatCurrency(data.budgetedCost)}
              </Text>
            </HStack>
            <HStack justify="space-between">
              <Text fontSize="xs" color={textSecondary}>
                Consumed:
              </Text>
              <Text fontSize="xs" color={textSecondary}>
                {formatCurrency(data.consumedCost)}
              </Text>
            </HStack>
          </VStack>
        </Box>
      );
    }
    return null;
  };

  // Enhanced financial calculations
  const calculateCompanyFinancials = (stats) => {
    const companyStats = stats?.projects || {};

    // Cost performance calculations
    const totalBudgetedCost = companyStats.totalBudgetedCost || 0;
    const totalConsumedCost = companyStats.totalConsumedCost || 0;
    const overallProgress = companyStats.overallProgress || 0;

    const earnedValue = totalBudgetedCost * (overallProgress / 100);
    const costPerformanceIndex =
      totalConsumedCost > 0 ? earnedValue / totalConsumedCost : 1;
    const costVariance = totalBudgetedCost - totalConsumedCost;
    const costVariancePercentage =
      totalBudgetedCost > 0 ? (costVariance / totalBudgetedCost) * 100 : 0;

    console.log('companystats', companyStats);
    // const totalEarnedManhours = 

    // Budget utilization
    const budgetUtilization =
      totalBudgetedCost > 0 ? (totalConsumedCost / totalBudgetedCost) * 100 : 0;

    // Profit/Loss calculations
    const estimatedRevenue = totalBudgetedCost * 1.2; // Assuming 20% markup
    const actualProfit = estimatedRevenue - totalConsumedCost;
    const projectedProfit = estimatedRevenue - totalBudgetedCost;
    const profitMargin =
      estimatedRevenue > 0 ? (actualProfit / estimatedRevenue) * 100 : 0;

    return {
      earnedValue,
      costPerformanceIndex,
      costVariance,
      costVariancePercentage,
      budgetUtilization,
      estimatedRevenue,
      actualProfit,
      projectedProfit,
      profitMargin,
      totalBudgetedCost,
      totalConsumedCost,
      overallProgress,
    };
  };

  // Filter projects
  const filteredProjects = useMemo(() => {
    if (!dashboardData?.projects) return [];

    return dashboardData.projects.filter((project) => {
      const matchesSearch =
        !searchQuery ||
        project.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        project.description?.toLowerCase().includes(searchQuery.toLowerCase());

      const matchesStatus =
        selectedStatus === "all" || project.status === selectedStatus;
      const matchesPriority =
        selectedPriority === "all" || project.priority === selectedPriority;

      return matchesSearch && matchesStatus && matchesPriority;
    });
  }, [dashboardData?.projects, searchQuery, selectedStatus, selectedPriority]);

  // Prepare chart data
  const prepareProjectStatusData = () => {
    if (!dashboardData?.projects) return [];

    const statusCount = dashboardData.projects.reduce((acc, project) => {
      acc[project.status] = (acc[project.status] || 0) + 1;
      return acc;
    }, {});

    return Object.keys(statusCount).map((status) => ({
      name: status.charAt(0).toUpperCase() + status.slice(1),
      value: statusCount[status],
      color: getStatusColor(status),
    }));
  };

  const prepareBudgetData = () => {
    if (!dashboardData?.projects) return [];

    return dashboardData.projects.slice(0, 6).map((project) => ({
      name:
        project.name.length > 15
          ? project.name.substring(0, 15) + "..."
          : project.name,
      budget: project.totalBudgetedCost,
      consumed: project.totalConsumedCost,
      earnedHours: project.totalEarnedManhours || 0,
      remaining: project.totalBudgetedCost - project.totalConsumedCost,
    }));
  };

  const prepareProgressData = () => {
    if (!dashboardData?.projects) return [];

    return dashboardData.projects.slice(0, 8).map((project) => ({
      name:
        project.name.length > 12
          ? project.name.substring(0, 12) + "..."
          : project.name,
      progress: project.progress,
      manHours: project.totalBudgetedHours || 0,
      consumedHours: project.totalConsumedHours || 0,
      budget: project.totalBudgetedCost,
      consumed: project.totalConsumedCost,
      earnedHours: project.totalEarnedManhours || 0,
      cpi: project.projectCPI || 0,
    }));
  };

  const prepareHealthRadarData = () => {
    if (!dashboardData?.projects || dashboardData.projects.length === 0)
      return [];

    // Calculate average health scores
    const healthMetrics = {
      schedule: 0,
      budget: 0,
      quality: 0,
      risk: 0,
      resources: 0,
    };

    const validProjects = dashboardData.projects.filter((p) => p.health);

    validProjects.forEach((project) => {
      const health = project.health;
      healthMetrics.schedule +=
        health?.schedule === "ahead" || health?.schedule === "on-track"
          ? 100
          : 50;
      healthMetrics.budget +=
        health?.budget === "under" || health?.budget === "on-track" ? 100 : 50;
      healthMetrics.quality +=
        health?.quality === "excellent" || health?.quality === "good"
          ? 100
          : 50;
      healthMetrics.risk +=
        health?.risk === "low" || health?.risk === "medium" ? 100 : 50;
      healthMetrics.resources += 75; // Default assumption
    });

    const count = validProjects.length || 1;

    return [
      {
        metric: "Schedule",
        value: healthMetrics.schedule / count,
        fullMark: 100,
      },
      { metric: "Budget", value: healthMetrics.budget / count, fullMark: 100 },
      {
        metric: "Quality",
        value: healthMetrics.quality / count,
        fullMark: 100,
      },
      { metric: "Risk", value: healthMetrics.risk / count, fullMark: 100 },
      {
        metric: "Resources",
        value: healthMetrics.resources / count,
        fullMark: 100,
      },
    ];
  };

  // Export dashboard data
  const handleExportReport = async () => {
    try {
      const csvContent = generateCSVReport();
      const blob = new Blob([csvContent], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `company-dashboard-${new Date().toISOString().split("T")[0]
        }.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success("Dashboard report exported successfully");
    } catch (error) {
      console.error("Export failed:", error);
      toast.error("Failed to export dashboard report");
    }
  };

  const generateCSVReport = () => {
    if (!dashboardData) return "";

    const { company, stats, projects } = dashboardData;
    const financials = calculateCompanyFinancials(stats);

    let csv = `Company Dashboard Report\n`;
    csv += `Company: ${company?.name || "Unknown"}\n`;
    csv += `Generated: ${new Date().toLocaleString()}\n\n`;

    csv += `FINANCIAL SUMMARY\n`;
    csv += `Total Budget,${formatCurrency(financials.totalBudgetedCost)}\n`;
    csv += `Total Consumed,${formatCurrency(financials.totalConsumedCost)}\n`;
    csv += `Cost Variance,${formatCurrency(financials.costVariance)}\n`;
    csv += `CPI,${(financials.costPerformanceIndex ?? 0).toFixed(2)}\n`;
    csv += `Estimated Revenue,${formatCurrency(financials.estimatedRevenue)}\n`;
    csv += `Projected Profit,${formatCurrency(financials.actualProfit)}\n`;
    csv += `Profit Margin,${(financials.profitMargin ?? 0).toFixed(1)}%\n\n`;

    if (projects && projects.length > 0) {
      csv += `PROJECT DETAILS\n`;
      csv += `Name,Status,Priority,Progress,CPI,Budget,Consumed,Profit\n`;
      projects.forEach((project) => {
        const projectProfit =
          project.totalBudgetedCost * 1.2 - project.totalConsumedCost;
        csv += `"${project.name}","${project.status}","${project.priority}",${project.progress
          }%,${project.projectCPI},"${formatCurrency(
            project.totalBudgetedCost,
            project.currency
          )}","${formatCurrency(
            project.totalConsumedCost,
            project.currency
          )}","${formatCurrency(projectProfit, project.currency)}"\n`;
      });
    }

    return csv;
  };

  // Debug component
  const DebugAuthState = () => {
    if (process.env.NODE_ENV !== "development") return null;

    return <Card bg="yellow.50" borderColor="yellow.200" mb={4}></Card>;
  };

  // Custom Tooltip for Charts
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;

      console.log("Tooltip data:", data);

      return (
        <Box
          bg={bgPrimary}
          p={4}
          borderRadius="lg"
          boxShadow="xl"
          border="1px"
          borderColor={borderColor}
          minW="280px"
        >
          <Text fontWeight="bold" mb={3} color={textPrimary}>
            {label}
          </Text>
          <VStack align="stretch" spacing={3}>
            {/* Manhours Section */}
            <Text fontSize="xs" color={textMuted} fontWeight="bold" textTransform="uppercase">
              Manhours
            </Text>

            <HStack justify="space-between">
              <Text fontSize="sm" color={textSecondary}>Budgeted Hours:</Text>
              <Text fontSize="sm" fontWeight="bold" color="#667eea">
                {formatNumber(data.manHours)} hrs
              </Text>
            </HStack>

            <HStack justify="space-between">
              <Text fontSize="sm" color={textSecondary}>Consumed Hours:</Text>
              <Text fontSize="sm" fontWeight="bold" color="#ef4444">
                {formatNumber(data.consumedHours)} hrs
              </Text>
            </HStack>

            {/* ✅ ADD EARNED HOURS */}
            <HStack justify="space-between">
              <Text fontSize="sm" color={textSecondary}>Earned Hours:</Text>
              <Text fontSize="sm" fontWeight="bold" color="#10b981">
                {formatNumber(data.earnedHours || 0)} hrs
              </Text>
            </HStack>

            <Divider />

            {/* Cost Section */}
            <Text fontSize="xs" color={textMuted} fontWeight="bold" textTransform="uppercase">
              Cost Analysis
            </Text>

            <HStack justify="space-between">
              <Text fontSize="sm" color={textSecondary}>Budget:</Text>
              <Text fontSize="sm" fontWeight="bold" color="#10b981">
                {formatCurrency(data.budget)}
              </Text>
            </HStack>

            <HStack justify="space-between">
              <Text fontSize="sm" color={textSecondary}>Consumed:</Text>
              <Text fontSize="sm" fontWeight="bold" color="#ef4444">
                {formatCurrency(data.consumed)}
              </Text>
            </HStack>

            <Divider />

            {/* Additional Info */}
            <HStack justify="space-between">
              <Text fontSize="xs" color={textSecondary}>Team Size:</Text>
              <Badge colorScheme="purple" variant="subtle">
                {data.teamSize} members
              </Badge>
            </HStack>
          </VStack>
        </Box>
      );
    }
    return null;
  };

  // Loading states
  if (authLoading) {
    return (
      <Box
        minH="100vh"
        bg={gradientBg}
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <VStack spacing={6}>
          <Spinner size="xl" color="blue.500" thickness="4px" />
          <Text color={textMuted} fontSize="lg" fontWeight="medium">
            Initializing dashboard...
          </Text>
        </VStack>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box
        minH="100vh"
        bg={gradientBg}
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <VStack spacing={6}>
          <Spinner size="xl" color="blue.500" thickness="4px" />
          <Text color={textMuted} fontSize="lg" fontWeight="medium">
            Loading dashboard data...
          </Text>
        </VStack>
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxW="full" p={6}>
        <DebugAuthState />
        <Alert status="error" borderRadius="xl" {...glassCard}>
          <AlertIcon />
          <VStack align="start" spacing={2}>
            <Text fontWeight="bold">Failed to load dashboard data</Text>
            <Text fontSize="sm">{error}</Text>
            <Button
              leftIcon={<FiRefreshCw />}
              size="sm"
              onClick={handleRefresh}
              colorScheme="red"
            >
              Retry
            </Button>
          </VStack>
        </Alert>
      </Container>
    );
  }

  if (!dashboardData) {
    return (
      <Container maxW="full" p={6}>
        <DebugAuthState />
        <Alert status="info" borderRadius="xl" {...glassCard}>
          <AlertIcon />
          <Text>No dashboard data available</Text>
        </Alert>
      </Container>
    );
  }

  const { company, stats, projects = [] } = dashboardData;
  const companyStats = stats?.projects || {};
  console.log("Company Stats:", companyStats);
  const financials = calculateCompanyFinancials(stats);

  return (
    <Box minH="100vh" bg={gradientBg} py={8}>
      <Container maxW="full" px={6}>
        <VStack spacing={8} align="stretch">
          <DebugAuthState />

          {/* Premium Header with Glassmorphism */}
          <Box
            borderRadius="2xl"
            p={8}
            {...glassCard}
            position="relative"
            overflow="hidden"
          >
            <Box
              position="absolute"
              top="-50%"
              right="-10%"
              w="400px"
              h="400px"
              bg="linear-gradient(135deg, rgba(102, 126, 234, 0.3) 0%, rgba(118, 75, 162, 0.3) 100%)"
              borderRadius="full"
              filter="blur(80px)"
              pointerEvents="none"
            />

            <Flex
              justify="space-between"
              align="center"
              wrap="wrap"
              gap={6}
              position="relative"
            >
              <VStack align="start" spacing={3}>
                <HStack spacing={4}>
                  <Box
                    p={3}
                    bg="linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
                    borderRadius="xl"
                    boxShadow="0 8px 24px rgba(102, 126, 234, 0.4)"
                  >
                    <Icon as={FiHome} color="white" boxSize={8} />
                  </Box>
                  <VStack align="start" spacing={1}>
                    <Heading
                      size="2xl"
                      bgGradient="linear(to-r, #667eea, #764ba2)"
                      bgClip="text"
                      fontWeight="extrabold"
                    >
                      {company?.name || "Company"} Dashboard
                    </Heading>
                    <HStack>
                      <Badge
                        colorScheme="green"
                        variant="solid"
                        fontSize="sm"
                        px={3}
                        py={1}
                        borderRadius="full"
                        boxShadow="0 4px 12px rgba(16, 185, 129, 0.3)"
                      >
                        <HStack spacing={1}>
                          <Box
                            w={2}
                            h={2}
                            bg="green.200"
                            borderRadius="full"
                            animation="pulse 2s infinite"
                          />
                          <Text>Live</Text>
                        </HStack>
                      </Badge>
                      <IconButton
                        icon={colorMode === "light" ? <FiMoon /> : <FiSun />}
                        onClick={toggleColorMode}
                        variant="ghost"
                        size="md"
                        aria-label="Toggle theme"
                        color={textSecondary}
                        _hover={{ bg: hoverBg, transform: "scale(1.1)" }}
                        transition="all 0.2s"
                      />
                    </HStack>
                  </VStack>
                </HStack>
                <Text color={textMuted} fontSize="lg" maxW="600px">
                  Welcome back
                  {user ? `, ${user.firstName} ${user.lastName}` : ""}!
                  Comprehensive company analytics and project insights at your
                  fingertips.
                </Text>
              </VStack>

              <HStack spacing={4}>
                <Button
                  leftIcon={<FiRefreshCw />}
                  variant="outline"
                  size="lg"
                  onClick={handleRefresh}
                  isLoading={refreshing}
                  borderRadius="xl"
                  borderWidth="2px"
                  _hover={{ transform: "translateY(-2px)", boxShadow: "lg" }}
                  transition="all 0.2s"
                >
                  Refresh
                </Button>
                {/* <Button
                  leftIcon={<FiDownload />}
                  bgGradient="linear(to-r, #667eea, #764ba2)"
                  color="white"
                  size="lg"
                  onClick={handleExportReport}
                  borderRadius="xl"
                  _hover={{
                    bgGradient: "linear(to-r, #764ba2, #667eea)",
                    transform: "translateY(-2px)",
                    boxShadow: "0 12px 24px rgba(102, 126, 234, 0.4)",
                  }}
                  transition="all 0.2s"
                >
                  Export Report
                </Button> */}
              </HStack>
            </Flex>
          </Box>

          {/* Project Performance Overview - Moved to Top */}
          <Box borderRadius="2xl" {...glassCard} p={6}>
            <HStack spacing={3} mb={4}>
              <Box
                p={3}
                bgGradient="linear(to-r, #667eea, #764ba2)"
                borderRadius="xl"
              >
                <Icon as={FiPieChart} color="white" boxSize={6} />
              </Box>
              <VStack align="start" spacing={0}>
                <Heading size="lg" color={textPrimary}>
                  Project Performance Overview
                </Heading>
                <Text fontSize="sm" color={textMuted}>
                  Comprehensive project metrics and performance indicators
                </Text>
              </VStack>
            </HStack>
            <ProjectDashboardTable dashboardData={dashboardData} />
          </Box>

          {/* Enhanced Tabs with Premium Design */}
          <Tabs
            index={selectedTabIndex}
            onChange={setSelectedTabIndex}
            variant="unstyled"
            colorScheme="purple"
          >
            <Box borderRadius="2xl" {...glassCard} p={2} mb={6}>
              <TabList>
                <Tab
                  borderRadius="xl"
                  fontWeight="semibold"
                  color={textSecondary}
                  _selected={{
                    color: "white",
                    bgGradient: "linear(to-r, #667eea, #764ba2)",
                    boxShadow: "0 4px 12px rgba(102, 126, 234, 0.4)",
                  }}
                  transition="all 0.2s"
                >
                  <HStack spacing={2}>
                    <Icon as={FiActivity} />
                    <Text>Overview</Text>
                  </HStack>
                </Tab>
                <Tab
                  borderRadius="xl"
                  fontWeight="semibold"
                  color={textSecondary}
                  _selected={{
                    color: "white",
                    bgGradient: "linear(to-r, #667eea, #764ba2)",
                    boxShadow: "0 4px 12px rgba(102, 126, 234, 0.4)",
                  }}
                  transition="all 0.2s"
                >
                  <HStack spacing={2}>
                    <Icon as={FiBarChart2} />
                    <Text>Analytics</Text>
                  </HStack>
                </Tab>
                <Tab
                  borderRadius="xl"
                  fontWeight="semibold"
                  color={textSecondary}
                  _selected={{
                    color: "white",
                    bgGradient: "linear(to-r, #667eea, #764ba2)",
                    boxShadow: "0 4px 12px rgba(102, 126, 234, 0.4)",
                  }}
                  transition="all 0.2s"
                >
                  <HStack spacing={2}>
                    <Icon as={FiDollarSign} />
                    <Text>Financial</Text>
                  </HStack>
                </Tab>
                <Tab
                  borderRadius="xl"
                  fontWeight="semibold"
                  color={textSecondary}
                  _selected={{
                    color: "white",
                    bgGradient: "linear(to-r, #667eea, #764ba2)",
                    boxShadow: "0 4px 12px rgba(102, 126, 234, 0.4)",
                  }}
                  transition="all 0.2s"
                >
                  <HStack spacing={2}>
                    <Icon as={FiPieChart} />
                    <Text>Projects</Text>
                  </HStack>
                </Tab>
              </TabList>
            </Box>

            <TabPanels>
              {/* Overview Tab */}
              <TabPanel p={0}>
                <VStack spacing={8} align="stretch">
                  {/* Company Health & Tasks Grid */}
                  <Grid
                    templateColumns={{ base: "1fr", lg: "2fr 1fr" }}
                    gap={8}
                  >
                    {/* Health Metrics Card */}
                    <Box borderRadius="2xl" {...glassCard} p={8}>
                      <VStack align="stretch" spacing={6}>
                        <HStack justify="space-between">
                          <HStack spacing={3}>
                            <Box
                              p={3}
                              bgGradient="linear(to-r, #667eea, #764ba2)"
                              borderRadius="xl"
                            >
                              <Icon as={FiActivity} color="white" boxSize={6} />
                            </Box>
                            <VStack align="start" spacing={0}>
                              <Heading size="lg" color={textPrimary}>
                                Company Health
                              </Heading>
                              <Text fontSize="sm" color={textMuted}>
                                Real-time performance metrics
                              </Text>
                            </VStack>
                          </HStack>
                          <Badge
                            colorScheme="green"
                            variant="subtle"
                            fontSize="md"
                            px={4}
                            py={2}
                            borderRadius="full"
                          >
                            {/* <HStack>
                              <Box
                                w={2}
                                h={2}
                                bg="green.400"
                                borderRadius="full"
                              />
                              <Text>Healthy</Text>
                            </HStack> */}
                          </Badge>
                        </HStack>

                        <VStack spacing={5} pt={4}>
                          {[
                            {
                              label: "Timeline Status",
                              value:
                                companyStats.overallProgress > 70
                                  ? "Ahead of schedule"
                                  : companyStats.overallProgress > 50
                                    ? "On track"
                                    : "Behind schedule",
                              icon: FiClock,
                              color:
                                companyStats.overallProgress > 70
                                  ? "green"
                                  : companyStats.overallProgress > 50
                                    ? "blue"
                                    : "orange",
                            },
                            {
                              label: "Pending Tasks",
                              value: `${(companyStats.totalTasks || 0) -
                                (companyStats.completedTasks || 0)
                                } tasks remaining`,
                              icon: FiCheckCircle,
                              color: "purple",
                            },
                            {
                              label: "Overall Progress",
                              value: `${((companyStats.overallProgress) ?? 0).toFixed(1) || 0
                                }% complete`,
                              icon: FiTarget,
                              color: "blue",
                            },
                          ].map((item, index) => (
                            <HStack
                              key={index}
                              justify="space-between"
                              w="100%"
                              p={4}
                              borderRadius="xl"
                              bg={bgSecondary}
                              _hover={{
                                bg: bgTertiary,
                                transform: "translateX(4px)",
                              }}
                              transition="all 0.2s"
                            >
                              <HStack spacing={3}>
                                <Box
                                  p={2}
                                  bg={`${item.color}.100`}
                                  borderRadius="lg"
                                >
                                  <Icon
                                    as={item.icon}
                                    color={`${item.color}.500`}
                                    boxSize={5}
                                  />
                                </Box>
                                <Text color={textMuted} fontWeight="medium">
                                  {item.label}
                                </Text>
                              </HStack>
                              <Text
                                fontWeight="bold"
                                color={`${item.color}.500`}
                                fontSize="lg"
                              >
                                {item.value}
                              </Text>
                            </HStack>
                          ))}
                        </VStack>
                      </VStack>
                    </Box>

                    {/* Tasks Overview with Enhanced Circular Progress */}
                    <Box borderRadius="2xl" {...glassCard} p={8}>
                      <VStack spacing={6}>
                        <Heading size="md" color={textPrimary}>
                          Tasks Overview
                        </Heading>

                        <Box position="relative">
                          <CircularProgress
                            value={companyStats.overallProgress || 0}
                            size="180px"
                            thickness="12px"
                            color="purple.400"
                            trackColor={bgTertiary}
                          >
                            <CircularProgressLabel>
                              <VStack spacing={1}>
                                <Text
                                  fontSize="4xl"
                                  fontWeight="bold"
                                  color={textPrimary}
                                >
                                  {Math.round(
                                    companyStats.overallProgress || 0
                                  )}
                                </Text>
                                <Text
                                  fontSize="sm"
                                  color={textMuted}
                                  fontWeight="medium"
                                >
                                  Complete
                                </Text>
                              </VStack>
                            </CircularProgressLabel>
                          </CircularProgress>
                        </Box>

                        <VStack spacing={3} w="100%" pt={4}>
                          {[
                            {
                              label: "Not Started",
                              count:
                                (companyStats.totalTasks || 0) -
                                (companyStats.completedTasks || 0) -
                                (companyStats.inProgressTasks || 0),
                              color: "gray",
                            },
                            {
                              label: "In Progress",
                              count: companyStats.inProgressTasks || 0,
                              color: "blue",
                            },
                            {
                              label: "Completed",
                              count: companyStats.completedTasks || 0,
                              color: "green",
                            },
                          ].map((item, index) => (
                            <HStack
                              key={index}
                              justify="space-between"
                              w="100%"
                              p={3}
                              borderRadius="lg"
                              bg={bgSecondary}
                            >
                              <HStack spacing={3}>
                                <Box
                                  w={4}
                                  h={4}
                                  bg={`${item.color}.400`}
                                  borderRadius="md"
                                />
                                <Text
                                  fontSize="sm"
                                  color={textMuted}
                                  fontWeight="medium"
                                >
                                  {item.label}
                                </Text>
                              </HStack>
                              <Text
                                fontSize="lg"
                                fontWeight="bold"
                                color={textPrimary}
                              >
                                {item.count}
                              </Text>
                            </HStack>
                          ))}
                        </VStack>
                      </VStack>
                    </Box>
                  </Grid>

                  {/* NEW: Project Resource Analysis - Manpower, Budget & Cost */}
                  <Box borderRadius="2xl" {...glassCard} p={8}>
                    <VStack align="stretch" spacing={6}>
                      <HStack justify="space-between">
                        <HStack spacing={3}>
                          <Box
                            p={3}
                            bgGradient="linear(to-r, #667eea, #764ba2)"
                            borderRadius="xl"
                          >
                            <Icon as={FiUsers} color="white" boxSize={6} />
                          </Box>
                          <VStack align="start" spacing={0}>
                            <Heading size="lg" color={textPrimary}>
                              Project Resources & Budget Analysis
                            </Heading>
                            <Text fontSize="sm" color={textMuted}>
                              Comprehensive view of manpower, budget allocation
                              and cost consumption
                            </Text>
                          </VStack>
                        </HStack>
                      </HStack>

                      <ResponsiveContainer width="100%" height={400}>
                        <ComposedChart data={prepareProjectResourceData()}>
                          <defs>
                            <linearGradient
                              id="manHoursGradient"
                              x1="0"
                              y1="0"
                              x2="0"
                              y2="1"
                            >
                              <stop
                                offset="5%"
                                stopColor="#667eea"
                                stopOpacity={0.9}
                              />
                              <stop
                                offset="95%"
                                stopColor="#764ba2"
                                stopOpacity={0.7}
                              />
                            </linearGradient>
                            <linearGradient
                              id="consumedHoursGradient"
                              x1="0"
                              y1="0"
                              x2="0"
                              y2="1"
                            >
                              <stop
                                offset="5%"
                                stopColor="#f59e0b"
                                stopOpacity={0.9}
                              />
                              <stop
                                offset="95%"
                                stopColor="#fbbf24"
                                stopOpacity={0.7}
                              />
                            </linearGradient>
                          </defs>
                          <CartesianGrid
                            strokeDasharray="3 3"
                            stroke={chartGridColor}
                          />
                          <XAxis
                            dataKey="name"
                            stroke={chartTextColor}
                            tick={{ fill: chartTextColor, fontSize: 11 }}
                          />
                          <YAxis
                            yAxisId="left"
                            stroke={chartTextColor}
                            tick={{ fill: chartTextColor }}
                            label={{
                              value: "Hours",
                              angle: -90,
                              position: "insideLeft",
                              fill: chartTextColor,
                            }}
                          />
                          <YAxis
                            yAxisId="right"
                            orientation="right"
                            stroke={chartTextColor}
                            tick={{ fill: chartTextColor }}
                            label={{
                              value: "Cost",
                              angle: 90,
                              position: "insideRight",
                              fill: chartTextColor,
                            }}
                          />
                          <RechartsTooltip content={<CustomTooltip />} />
                          <Legend wrapperStyle={{ paddingTop: "20px" }} />
                          <Bar
                            yAxisId="left"
                            dataKey="manHours"
                            fill="url(#manHoursGradient)"
                            name="Budgeted Hours"
                          />
                          <Bar
                            yAxisId="left"
                            dataKey="consumedHours"
                            fill="url(#consumedHoursGradient)"
                            name="Consumed Hours"
                          />
                          <Line
                            yAxisId="right"
                            type="monotone"
                            dataKey="budget"
                            stroke="#10b981"
                            strokeWidth={3}
                            name="Budget"
                            dot={{ fill: "#10b981", strokeWidth: 2, r: 5 }}
                          />
                          <Line
                            yAxisId="right"
                            type="monotone"
                            dataKey="consumed"
                            stroke="#ef4444"
                            strokeWidth={3}
                            name="Consumed Cost"
                            dot={{ fill: "#ef4444", strokeWidth: 2, r: 5 }}
                            strokeDasharray="5 5"
                          />
                        </ComposedChart>
                      </ResponsiveContainer>

                      <HStack spacing={8} justify="center" flexWrap="wrap">
                        <HStack spacing={2}>
                          <Box
                            w={4}
                            h={4}
                            bgGradient="linear(to-b, #667eea, #764ba2)"
                            borderRadius="md"
                          />
                          <Text fontSize="sm" color={textSecondary}>
                            Budgeted Hours
                          </Text>
                        </HStack>
                        <HStack spacing={2}>
                          <Box
                            w={4}
                            h={4}
                            bgGradient="linear(to-b, #f59e0b, #fbbf24)"
                            borderRadius="md"
                          />
                          <Text fontSize="sm" color={textSecondary}>
                            Consumed Hours
                          </Text>
                        </HStack>
                        <HStack spacing={2}>
                          <Box w={4} h={4} bg="#10b981" borderRadius="md" />
                          <Text fontSize="sm" color={textSecondary}>
                            Budget Cost
                          </Text>
                        </HStack>
                        <HStack spacing={2}>
                          <Box w={4} h={4} bg="#ef4444" borderRadius="md" />
                          <Text fontSize="sm" color={textSecondary}>
                            Consumed Cost
                          </Text>
                        </HStack>
                      </HStack>
                    </VStack>
                  </Box>
                </VStack>
              </TabPanel>

              {/* Analytics Tab */}
              <TabPanel p={0}>
                <VStack spacing={8} align="stretch">
                  {/* Project Progress Bar Chart */}
                  <Box borderRadius="2xl" {...glassCard} p={8}>
                    <VStack align="stretch" spacing={6}>
                      <HStack justify="space-between">
                        <HStack spacing={3}>
                          <Box
                            p={3}
                            bgGradient="linear(to-r, #10b981, #34d399)"
                            borderRadius="xl"
                          >
                            <Icon as={FiBarChart2} color="white" boxSize={6} />
                          </Box>
                          <VStack align="start" spacing={0}>
                            <Heading size="lg" color={textPrimary}>
                              Project Progress Analysis
                            </Heading>
                            <Text fontSize="sm" color={textMuted}>
                              Progress vs CPI comparison
                            </Text>
                          </VStack>
                        </HStack>
                      </HStack>

                      <ResponsiveContainer width="100%" height={400}>
                        <ComposedChart data={prepareProgressData()}>
                          <defs>
                            <linearGradient
                              id="colorProgress"
                              x1="0"
                              y1="0"
                              x2="0"
                              y2="1"
                            >
                              <stop
                                offset="5%"
                                stopColor="#667eea"
                                stopOpacity={0.8}
                              />
                              <stop
                                offset="95%"
                                stopColor="#764ba2"
                                stopOpacity={0.3}
                              />
                            </linearGradient>
                          </defs>
                          <CartesianGrid
                            strokeDasharray="3 3"
                            stroke={borderColor}
                          />
                          <XAxis
                            dataKey="name"
                            stroke={textMuted}
                            tick={{ fill: textPrimary }}
                          />
                          <YAxis
                            stroke={textMuted}
                            tick={{ fill: textPrimary }}
                          />
                          <RechartsTooltip content={<CustomTooltip />} />
                          <Legend />
                          <Bar
                            dataKey="progress"
                            fill="url(#colorProgress)"
                            name="Progress"
                          />
                          <Line
                            type="monotone"
                            dataKey="cpi"
                            stroke="#f59e0b"
                            strokeWidth={3}
                            name="CPI"
                            dot={{ fill: "#f59e0b", strokeWidth: 2, r: 5 }}
                          />
                        </ComposedChart>
                      </ResponsiveContainer>
                    </VStack>
                  </Box>

                  {/* Project Health Radar Chart */}
                  <Grid
                    templateColumns={{ base: "1fr", lg: "1fr 1fr" }}
                    gap={8}
                  >
                    <Box borderRadius="2xl" {...glassCard} p={8}>
                      <VStack align="stretch" spacing={6}>
                        <HStack spacing={3}>
                          <Box
                            p={3}
                            bgGradient="linear(to-r, #8b5cf6, #a78bfa)"
                            borderRadius="xl"
                          >
                            <Icon as={FiTarget} color="white" boxSize={6} />
                          </Box>
                          <VStack align="start" spacing={0}>
                            <Heading size="lg" color={textPrimary}>
                              Health Metrics
                            </Heading>
                            <Text fontSize="sm" color={textPrimary}>
                              Overall performance radar
                            </Text>
                          </VStack>
                        </HStack>

                        <ResponsiveContainer width="100%" height={350}>
                          <RadarChart data={prepareHealthRadarData()}>
                            <PolarGrid stroke={borderColor} />
                            <PolarAngleAxis
                              dataKey="metric"
                              tick={{ fill: textPrimary, fontSize: 12 }}
                            />
                            <PolarRadiusAxis
                              angle={90}
                              domain={[0, 100]}
                              tick={{ fill: textPrimary }}
                            />
                            <Radar
                              name="Performance"
                              dataKey="value"
                              stroke="#667eea"
                              fill="#667eea"
                              fillOpacity={0.6}
                            />
                            <RechartsTooltip content={<CustomTooltip />} />
                          </RadarChart>
                        </ResponsiveContainer>
                      </VStack>
                    </Box>

                    {/* Budget Consumption Area Chart */}
                    <Box borderRadius="2xl" {...glassCard} p={8}>
                      <VStack align="stretch" spacing={6}>
                        <HStack spacing={3}>
                          <Box
                            p={3}
                            bgGradient="linear(to-r, #f59e0b, #fbbf24)"
                            borderRadius="xl"
                          >
                            <Icon as={FiDollarSign} color="white" boxSize={6} />
                          </Box>
                          <VStack align="start" spacing={0}>
                            <Heading size="lg" color={textPrimary}>
                              Budget Trends
                            </Heading>
                            <Text fontSize="sm" color={textMuted}>
                              Budget vs consumed
                            </Text>
                          </VStack>
                        </HStack>

                        <ResponsiveContainer width="100%" height={350}>
                          <AreaChart data={prepareBudgetData()}>
                            <defs>
                              <linearGradient
                                id="colorBudget"
                                x1="0"
                                y1="0"
                                x2="0"
                                y2="1"
                              >
                                <stop
                                  offset="5%"
                                  stopColor="#667eea"
                                  stopOpacity={0.8}
                                />
                                <stop
                                  offset="95%"
                                  stopColor="#667eea"
                                  stopOpacity={0.1}
                                />
                              </linearGradient>
                              <linearGradient
                                id="colorConsumed"
                                x1="0"
                                y1="0"
                                x2="0"
                                y2="1"
                              >
                                <stop
                                  offset="5%"
                                  stopColor="#ef4444"
                                  stopOpacity={0.8}
                                />
                                <stop
                                  offset="95%"
                                  stopColor="#ef4444"
                                  stopOpacity={0.1}
                                />
                              </linearGradient>
                            </defs>
                            <CartesianGrid
                              strokeDasharray="3 3"
                              stroke={borderColor}
                            />
                            <XAxis
                              dataKey="name"
                              stroke={textPrimary}
                              tick={{ fill: textPrimary, fontSize: 11 }}
                            />
                            <YAxis
                              stroke={textPrimary}
                              tick={{ fill: textPrimary }}
                            />
                            <RechartsTooltip content={<CustomTooltip />} />
                            <Legend />
                            <Area
                              type="monotone"
                              dataKey="budget"
                              stroke="#667eea"
                              fillOpacity={1}
                              fill="url(#colorBudget)"
                              name="Budget"
                            />
                            <Area
                              type="monotone"
                              dataKey="consumed"
                              stroke="#ef4444"
                              fillOpacity={1}
                              fill="url(#colorConsumed)"
                              name="Consumed"
                            />
                          </AreaChart>
                        </ResponsiveContainer>
                      </VStack>
                    </Box>
                  </Grid>
                </VStack>
              </TabPanel>

              {/* Financial Tab - Manhours First */}
              <TabPanel p={0}>
                <VStack spacing={8} align="stretch">
                  <HStack spacing={3} mb={4}>
                    <Box
                      p={3}
                      bgGradient="linear(to-r, #667eea, #764ba2)"
                      borderRadius="xl"
                    >
                      <Icon as={FiClock} color="white" boxSize={8} />
                    </Box>
                    <VStack align="start" spacing={0}>
                      <Heading size="xl" color={textPrimary}>
                        Performance Dashboard
                      </Heading>
                      <Text fontSize="md" color={textMuted}>
                        Manhour-based performance analysis and cost tracking
                      </Text>
                    </VStack>
                  </HStack>

                  {/* Enhanced Performance Cards - Manhours First */}
                  <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={6}>
                    {/* Total Budgeted Manhours Card */}
                    <Box
                      borderRadius="2xl"
                      bgGradient="linear(135deg, #667eea 0%, #764ba2 100%)"
                      color="white"
                      p={6}
                      boxShadow="0 8px 32px rgba(102, 126, 234, 0.4)"
                      _hover={{ transform: "translateY(-4px)" }}
                      transition="all 0.3s"
                    >
                      <VStack align="start" spacing={4}>
                        <HStack justify="space-between" w="100%">
                          <Box p={2} bg="whiteAlpha.300" borderRadius="lg">
                            <Icon as={FiClock} boxSize={6} />
                          </Box>
                          <Badge
                            bg="whiteAlpha.300"
                            px={3}
                            py={1}
                            borderRadius="full"
                          >
                            BUDGETED
                          </Badge>
                        </HStack>
                        <VStack align="start" spacing={1}>
                          <Text fontSize="sm" opacity={0.9} fontWeight="medium">
                            Total Budgeted Manhours
                          </Text>
                          <Text fontSize="3xl" fontWeight="bold">
                            {formatNumber(companyStats.totalBudgetedHours || 0)} hrs
                          </Text>
                          <Text fontSize="xs" opacity={0.8}>
                            {formatDetailedCurrency(financials.totalBudgetedCost)} cost
                          </Text>
                        </VStack>
                      </VStack>
                    </Box>

                    {/* Consumed Manhours Card */}
                    <Box
                      borderRadius="2xl"
                      bgGradient="linear(135deg, #3271cfff 0%, #53b3f3ff 100%)"
                      color="white"
                      p={6}
                      boxShadow="0 8px 32px rgba(74, 155, 248, 0.4)"
                      _hover={{ transform: "translateY(-4px)" }}
                      transition="all 0.3s"
                    >
                      <VStack align="start" spacing={4}>
                        <HStack justify="space-between" w="100%">
                          <Box p={2} bg="whiteAlpha.300" borderRadius="lg">
                            <Icon as={FiActivity} boxSize={6} />
                          </Box>
                          <Badge
                            bg="whiteAlpha.300"
                            px={3}
                            py={1}
                            borderRadius="full"
                          >
                            CONSUMED
                          </Badge>
                        </HStack>
                        <VStack align="start" spacing={1}>
                          <Text fontSize="sm" opacity={0.9} fontWeight="medium">
                            Total Consumed Manhours
                          </Text>
                          <Text fontSize="3xl" fontWeight="bold">
                            {formatNumber(companyStats.totalConsumedHours || 0)} hrs
                          </Text>
                          <Text fontSize="xs" opacity={0.8}>
                            {formatDetailedCurrency(financials.totalConsumedCost)} cost •{" "}
                            {((companyStats.totalConsumedHours / (companyStats.totalBudgetedHours || 1)) * 100).toFixed(1)}% utilized
                          </Text>
                        </VStack>
                      </VStack>
                    </Box>

                    {/* Earned Manhours Card - UPDATED: Uses Installed Qty × Productivity */}
                    <Box
                      borderRadius="2xl"
                      bgGradient="linear(135deg, #10b981 0%, #34d399 100%)"
                      color="white"
                      p={6}
                      boxShadow="0 8px 32px rgba(16, 185, 129, 0.4)"
                      _hover={{ transform: "translateY(-4px)" }}
                      transition="all 0.3s"
                    >
                      <VStack align="start" spacing={4}>
                        <HStack justify="space-between" w="100%">
                          <Box p={2} bg="whiteAlpha.300" borderRadius="lg">
                            <Icon as={FiTarget} boxSize={6} />
                          </Box>
                          <Badge
                            bg="whiteAlpha.300"
                            px={3}
                            py={1}
                            borderRadius="full"
                          >
                            EARNED
                          </Badge>
                        </HStack>
                        <VStack align="start" spacing={1}>
                          <Text fontSize="sm" opacity={0.9} fontWeight="medium">
                            Earned Manhours
                          </Text>
                          <Text fontSize="3xl" fontWeight="bold">
                            {formatNumber(
                              projects.reduce((sum, p) => sum + (p.startedEarnedManhours || 0), 0)
                            )} hrs
                          </Text>
                          <Text fontSize="xs" opacity={0.8}>
                            Installed Qty × Productivity •{" "}
                            {companyStats.totalStartedTasks || 0} started tasks
                          </Text>
                        </VStack>
                      </VStack>
                    </Box>

                    {/* Manhour Variance Card - UPDATED: Uses correct earned manhours */}
                    <Box
                      borderRadius="2xl"
                      bgGradient={
                        (() => {
                          const earnedManhours = projects.reduce((sum, p) => sum + (p.startedEarnedManhours || 0), 0);
                          const consumedManhours = companyStats.totalConsumedHours || 0;
                          const variance = earnedManhours - consumedManhours;
                          const variancePercentage = consumedManhours > 0 ? Math.abs(variance / consumedManhours) * 100 : 0;

                          if (variance >= 0) {
                            // Positive variance - Green gradient
                            return "linear(135deg, #10b981 0%, #34d399 100%)";
                          } else {
                            // Negative variance - Red gradient based on severity
                            if (variancePercentage < 10) {
                              // Slight negative (< 10%) - Light red/pink
                              return "linear(135deg, #fb7185 0%, #fda4af 100%)";
                            } else if (variancePercentage < 20) {
                              // Moderate negative (10-20%) - Medium red/orange-red
                              return "linear(135deg, #f43f5e 0%, #fb7185 100%)";
                            } else if (variancePercentage < 30) {
                              // High negative (20-30%) - Deep red
                              return "linear(135deg, #e11d48 0%, #f43f5e 100%)";
                            } else {
                              // Critical negative (> 30%) - Very deep red
                              return "linear(135deg, #be123c 0%, #e11d48 100%)";
                            }
                          }
                        })()
                      }
                      color="white"
                      p={6}
                      boxShadow={
                        (() => {
                          const earnedManhours = projects.reduce((sum, p) => sum + (p.startedEarnedManhours || 0), 0);
                          const consumedManhours = companyStats.totalConsumedHours || 0;
                          const variance = earnedManhours - consumedManhours;
                          const variancePercentage = consumedManhours > 0 ? Math.abs(variance / consumedManhours) * 100 : 0;

                          if (variance >= 0) {
                            return "0 8px 32px rgba(16, 185, 129, 0.4)";
                          } else {
                            // Adjust shadow intensity based on severity
                            if (variancePercentage < 10) {
                              return "0 8px 32px rgba(251, 113, 133, 0.3)";
                            } else if (variancePercentage < 20) {
                              return "0 8px 32px rgba(244, 63, 94, 0.4)";
                            } else if (variancePercentage < 30) {
                              return "0 8px 32px rgba(225, 29, 72, 0.5)";
                            } else {
                              return "0 8px 32px rgba(190, 18, 60, 0.6)";
                            }
                          }
                        })()
                      }
                      _hover={{ transform: "translateY(-4px)" }}
                      transition="all 0.3s"
                    >
                      <VStack align="start" spacing={4}>
                        <HStack justify="space-between" w="100%">
                          <Box p={2} bg="whiteAlpha.300" borderRadius="lg">
                            <Icon
                              as={
                                (() => {
                                  const earnedManhours = projects.reduce((sum, p) => sum + (p.startedEarnedManhours || 0), 0);
                                  const consumedManhours = companyStats.totalConsumedHours || 0;
                                  const variance = earnedManhours - consumedManhours;
                                  return variance >= 0 ? FiTrendingUp : FiTrendingDown;
                                })()
                              }
                              boxSize={6}
                            />
                          </Box>
                          {/* <Badge
                            bg="whiteAlpha.300"
                            px={3}
                            py={1}
                            borderRadius="full"
                          >
                            CPI {companyStats.overallCPI?.toFixed(2) || "1.00"}
                          </Badge> */}
                        </HStack>
                        <VStack align="start" spacing={1}>
                          <Text fontSize="sm" opacity={0.9} fontWeight="medium">
                            Manhour Variance
                          </Text>
                          <Text fontSize="3xl" fontWeight="bold">
                            {(() => {
                              const earnedManhours = projects.reduce((sum, p) => sum + (p.startedEarnedManhours || 0), 0);
                              const consumedManhours = companyStats.totalConsumedHours || 0;
                              const variance = earnedManhours - consumedManhours;
                              return `${variance >= 0 ? "+" : ""}${formatNumber(variance)} hrs`;
                            })()}
                          </Text>
                          <HStack spacing={2}>
                            <Text fontSize="xs" opacity={0.8}>
                              Based on started tasks
                            </Text>
                            {(() => {
                              const earnedManhours = projects.reduce((sum, p) => sum + (p.startedEarnedManhours || 0), 0);
                              const consumedManhours = companyStats.totalConsumedHours || 0;
                              const variance = earnedManhours - consumedManhours;
                              const variancePercentage = consumedManhours > 0 ? Math.abs(variance / consumedManhours) * 100 : 0;

                              if (variance < 0) {
                                return (
                                  <Badge
                                    bg="whiteAlpha.400"
                                    fontSize="2xs"
                                    px={2}
                                    py={0.5}
                                    borderRadius="md"
                                  >
                                    {(variancePercentage ?? 0).toFixed(1)}% over
                                  </Badge>
                                );
                              }
                              return null;
                            })()}
                          </HStack>
                        </VStack>
                      </VStack>
                    </Box>

                  </SimpleGrid>

                  {/* Manhours Comparison Bar Chart */}
                  <Box borderRadius="2xl" {...glassCard} p={8}>
                    <VStack align="stretch" spacing={6}>
                      <HStack spacing={3}>
                        <Box
                          p={3}
                          bgGradient="linear(to-r, #667eea, #764ba2)"
                          borderRadius="xl"
                        >
                          <Icon as={FiBarChart2} color="white" boxSize={6} />
                        </Box>
                        <VStack align="start" spacing={0}>
                          <Heading size="lg" color={textPrimary}>
                            Manhours vs Performance Analysis
                          </Heading>
                          <Text fontSize="sm" color={textPrimary}>
                            Project-wise manhour breakdown
                          </Text>
                        </VStack>
                      </HStack>

                      <ResponsiveContainer width="100%" height={400}>
                        <BarChart data={prepareManhoursData()}>
                          <defs>
                            <linearGradient
                              id="budgetedManhoursGradient"
                              x1="0"
                              y1="0"
                              x2="0"
                              y2="1"
                            >
                              <stop
                                offset="5%"
                                stopColor="#667eea"
                                stopOpacity={0.9}
                              />
                              <stop
                                offset="95%"
                                stopColor="#764ba2"
                                stopOpacity={0.7}
                              />
                            </linearGradient>
                            <linearGradient
                              id="consumedManhoursGradient"
                              x1="0"
                              y1="0"
                              x2="0"
                              y2="1"
                            >
                              <stop
                                offset="5%"
                                stopColor="#ef4444"
                                stopOpacity={0.9}
                              />
                              <stop
                                offset="95%"
                                stopColor="#f87171"
                                stopOpacity={0.7}
                              />
                            </linearGradient>
                            <linearGradient
                              id="earnedManhoursGradient"
                              x1="0"
                              y1="0"
                              x2="0"
                              y2="1"
                            >
                              <stop
                                offset="5%"
                                stopColor="#10b981"
                                stopOpacity={0.9}
                              />
                              <stop
                                offset="95%"
                                stopColor="#34d399"
                                stopOpacity={0.7}
                              />
                            </linearGradient>
                          </defs>
                          <CartesianGrid
                            strokeDasharray="3 3"
                            stroke={borderColor}
                          />
                          <XAxis
                            dataKey="name"
                            stroke={textPrimary}
                            tick={{ fill: textPrimary, fontSize: 11 }}
                          />
                          <YAxis
                            stroke={textPrimary}
                            tick={{ fill: textPrimary }}
                            label={{ value: 'Manhours', angle: -90, position: 'insideLeft' }}
                          />
                          <RechartsTooltip content={<CustomManhoursTooltip />} />
                          <Legend />
                          <Bar
                            dataKey="budgetedHours"
                            fill="url(#budgetedManhoursGradient)"
                            name="Budgeted Manhours"
                          />
                          <Bar
                            dataKey="consumedHours"
                            fill="url(#consumedManhoursGradient)"
                            name="Consumed Manhours"
                          />
                          <Bar
                            dataKey="earnedHours"
                            fill="url(#earnedManhoursGradient)"
                            name="Earned Manhours"
                          />
                        </BarChart>
                      </ResponsiveContainer>
                    </VStack>
                  </Box>

                  {/* Performance Summary Table - UPDATED: Uses correct earned manhours */}
                  <Box borderRadius="2xl" {...glassCard} overflow="hidden">
                    <Box bg={bgSecondary} p={6}>
                      <Heading size="md" color={textPrimary}>
                        Detailed Performance Metrics
                      </Heading>
                    </Box>
                    <Box overflowX="auto">
                      <Table variant="simple" size="md">
                        <Thead bg={bgSecondary}>
                          <Tr>
                            <Th color={textSecondary} fontSize="sm">
                              Metric
                            </Th>
                            <Th color={textSecondary} fontSize="sm" isNumeric>
                              Manhours
                            </Th>
                            <Th color={textSecondary} fontSize="sm" isNumeric>
                              Cost Amount
                            </Th>
                            <Th color={textSecondary} fontSize="sm" isNumeric>
                              Utilization %
                            </Th>
                            <Th color={textSecondary} fontSize="sm">
                              Performance
                            </Th>
                          </Tr>
                        </Thead>
                        <Tbody>
                          {[
                            {
                              metric: "Total Budgeted",
                              manhours: companyStats.totalBudgetedHours || 0,
                              cost: financials.totalBudgetedCost,
                              percentage: 100,
                              status: "Baseline",
                              color: "blue",
                            },
                            {
                              metric: "Total Consumed",
                              manhours: companyStats.totalConsumedHours || 0,
                              cost: financials.totalConsumedCost,
                              percentage: ((companyStats.totalConsumedHours || 0) / (companyStats.totalBudgetedHours || 1)) * 100,
                              status:
                                ((companyStats.totalConsumedHours || 0) / (companyStats.totalBudgetedHours || 1)) * 100 < 80
                                  ? "On Track"
                                  : ((companyStats.totalConsumedHours || 0) / (companyStats.totalBudgetedHours || 1)) * 100 < 100
                                    ? "Near Limit"
                                    : "Over Budget",
                              color: getProgressColor(
                                ((companyStats.totalConsumedHours || 0) / (companyStats.totalBudgetedHours || 1)) * 100
                              ),
                            },
                            {
                              metric: "Earned Manhours (Started Tasks)",
                              manhours: projects.reduce((sum, p) => sum + (p.startedEarnedManhours || 0), 0),
                              cost: null,
                              percentage: (projects.reduce((sum, p) => sum + (p.startedEarnedManhours || 0), 0) / (companyStats.totalBudgetedHours || 1)) * 100,
                              status: `${companyStats.totalStartedTasks || 0} tasks started`,
                              color: "green",
                            },
                            {
                              metric: "Manhour Variance (EV - AC)",
                              manhours: (projects.reduce((sum, p) => sum + (p.startedEarnedManhours || 0), 0)) - (companyStats.totalConsumedHours || 0),
                              cost: financials.costVariance,
                              percentage: ((projects.reduce((sum, p) => sum + (p.startedEarnedManhours || 0), 0)) - (companyStats.totalConsumedHours || 0)) / (companyStats.totalBudgetedHours || 1) * 100,
                              status:
                                ((projects.reduce((sum, p) => sum + (p.startedEarnedManhours || 0), 0)) - (companyStats.totalConsumedHours || 0)) >= 0
                                  ? "Efficient"
                                  : "Overrun",
                              color:
                                ((projects.reduce((sum, p) => sum + (p.startedEarnedManhours || 0), 0)) - (companyStats.totalConsumedHours || 0)) >= 0 ? "green" : "red",
                            },
                            {
                              metric: "Cost Performance Index (CPI)",
                              manhours: companyStats.overallCPI || 1.00,
                              cost: null,
                              percentage: (companyStats.overallCPI || 1) * 100,
                              status:
                                (companyStats.overallCPI || 1) >= 1.1 ? "Excellent" :
                                  (companyStats.overallCPI || 1) >= 0.9 ? "Good" :
                                    (companyStats.overallCPI || 1) >= 0.8 ? "Fair" : "Poor",
                              color:
                                (companyStats.overallCPI || 1) >= 1 ? "green" :
                                  (companyStats.overallCPI || 1) >= 0.8 ? "yellow" : "red",
                            },
                          ].map((row, index) => (
                            <Tr key={index} _hover={{ bg: hoverBg }}>
                              <Td color={textPrimary} fontWeight="medium">
                                {row.metric}
                              </Td>
                              <Td
                                isNumeric
                                color={textPrimary}
                                fontWeight="bold"
                              >
                                {row.metric === "Cost Performance Index (CPI)"
                                  ? (row.manhours ?? 0).toFixed(2)
                                  : `${formatNumber(row.manhours)} hrs`
                                }
                              </Td>
                              <Td
                                isNumeric
                                color={textSecondary}
                                fontSize="sm"
                              >
                                {row.cost !== null ? formatCurrency(row.cost) : "-"}
                              </Td>
                              <Td
                                isNumeric
                                color={textPrimary}
                                fontWeight="semibold"
                              >
                                {(row.percentage ?? 0).toFixed(1)}%
                              </Td>
                              <Td>
                                <Badge
                                  colorScheme={row.color}
                                  variant="subtle"
                                  fontSize="sm"
                                  px={3}
                                  py={1}
                                  borderRadius="full"
                                >
                                  {row.status}
                                </Badge>
                              </Td>
                            </Tr>
                          ))}
                        </Tbody>
                      </Table>
                    </Box>
                  </Box>
                </VStack>
              </TabPanel>

              {/* Projects Tab */}
              <TabPanel p={0}>
                <VStack spacing={8} align="stretch">
                  <HStack spacing={3} mb={4}>
                    <Box
                      p={3}
                      bgGradient="linear(to-r, #667eea, #764ba2)"
                      borderRadius="xl"
                    >
                      <Icon as={FiPieChart} color="white" boxSize={8} />
                    </Box>
                    <VStack align="start" spacing={0}>
                      <Heading size="xl" color={textPrimary}>
                        Project Portfolio
                      </Heading>
                      <Text fontSize="md" color={textMuted}>
                        Complete overview of all company projects
                      </Text>
                    </VStack>
                  </HStack>

                  {/* Projects Table */}
                  <Box borderRadius="2xl" {...glassCard} overflow="hidden">
                    <Box bg={bgSecondary} p={6}>
                      <Flex
                        justify="space-between"
                        align="center"
                        wrap="wrap"
                        gap={4}
                      >
                        <Heading size="md" color={textPrimary}>
                          Active Projects ({filteredProjects.length})
                        </Heading>

                        <HStack spacing={3}>
                          <FormControl maxW="250px">
                            <Input
                              placeholder="Search projects..."
                              value={searchQuery}
                              onChange={(e) => setSearchQuery(e.target.value)}
                              size="md"
                              bg={bgPrimary}
                              borderRadius="xl"
                              leftIcon={<FiSearch />}
                            />
                          </FormControl>

                          <Select
                            value={selectedStatus}
                            onChange={(e) => setSelectedStatus(e.target.value)}
                            size="md"
                            maxW="150px"
                            bg={bgPrimary}
                            borderRadius="xl"
                          >
                            <option value="all">All Status</option>
                            <option value="active">Active</option>
                            <option value="planning">Planning</option>
                            <option value="completed">Completed</option>
                          </Select>

                          <Select
                            value={selectedPriority}
                            onChange={(e) =>
                              setSelectedPriority(e.target.value)
                            }
                            size="md"
                            maxW="150px"
                            bg={bgPrimary}
                            borderRadius="xl"
                          >
                            <option value="all">All Priority</option>
                            <option value="high">High</option>
                            <option value="medium">Medium</option>
                            <option value="low">Low</option>
                          </Select>
                        </HStack>
                      </Flex>
                    </Box>

                    <Box overflowX="auto">
                      {filteredProjects.length === 0 ? (
                        <Alert status="info" m={6} borderRadius="xl">
                          <AlertIcon />
                          <VStack align="start" spacing={2}>
                            <Text fontWeight="bold">No projects found</Text>
                            <Text fontSize="sm">
                              Create your first project to get started with the
                              dashboard.
                            </Text>
                          </VStack>
                        </Alert>
                      ) : (
                        <Table variant="simple" size="md">
                          <Thead bg={bgSecondary}>
                            <Tr>
                              <Th color={textSecondary} fontSize="sm">
                                Project Name
                              </Th>
                              <Th color={textSecondary} fontSize="sm">
                                Status
                              </Th>
                              <Th color={textSecondary} fontSize="sm">
                                Priority
                              </Th>
                              <Th color={textSecondary} fontSize="sm" isNumeric>
                                Progress
                              </Th>
                              <Th color={textSecondary} fontSize="sm">
                                Tasks
                              </Th>
                              <Th color={textSecondary} fontSize="sm" isNumeric>
                                Budget
                              </Th>
                              <Th color={textSecondary} fontSize="sm">
                                Health
                              </Th>
                              <Th color={textSecondary} fontSize="sm">
                                Actions
                              </Th>
                            </Tr>
                          </Thead>
                          <Tbody>
                            {filteredProjects.map((project) => {
                              const projectProfit =
                                project.totalBudgetedCost * 1.2 -
                                project.totalConsumedCost;
                              const projectProfitMargin =
                                project.totalBudgetedCost > 0
                                  ? (projectProfit /
                                    (project.totalBudgetedCost * 1.2)) *
                                  100
                                  : 0;

                              return (
                                <Tr
                                  key={project.id}
                                  _hover={{ bg: hoverBg }}
                                  cursor="pointer"
                                  transition="all 0.2s"
                                >
                                  <Td>
                                    <VStack align="start" spacing={1}>
                                      <Text
                                        fontWeight="bold"
                                        color={textPrimary}
                                        fontSize="md"
                                      >
                                        {project.name}
                                      </Text>
                                      <Text fontSize="xs" color={textMuted}>
                                        {project.description}
                                      </Text>
                                    </VStack>
                                  </Td>

                                  <Td>
                                    <Badge
                                      colorScheme={getStatusColor(
                                        project.status
                                      )}
                                      variant="subtle"
                                      fontSize="sm"
                                      px={3}
                                      py={1}
                                      borderRadius="full"
                                    >
                                      {project.status}
                                    </Badge>
                                  </Td>

                                  <Td>
                                    <Badge
                                      colorScheme={getPriorityColor(
                                        project.priority
                                      )}
                                      variant="outline"
                                      fontSize="sm"
                                      px={3}
                                      py={1}
                                      borderRadius="full"
                                    >
                                      {project.priority}
                                    </Badge>
                                  </Td>

                                  <Td isNumeric>
                                    <VStack spacing={2} align="end">
                                      <Text
                                        fontSize="sm"
                                        fontWeight="bold"
                                        color={textPrimary}
                                      >
                                        {(project.progress || 0).toFixed(1)}
                                      </Text>
                                    </VStack>
                                  </Td>

                                  <Td>
                                    <VStack align="start" spacing={1}>
                                      <Text
                                        fontSize="md"
                                        fontWeight="bold"
                                        color={textPrimary}
                                      >
                                        {project.completedTasks}/
                                        {project.totalTasks}
                                      </Text>
                                      <Text fontSize="xs" color={textMuted}>
                                        {project.inProgressTasks} in progress
                                      </Text>
                                    </VStack>
                                  </Td>

                                  <Td isNumeric>
                                    <VStack align="end" spacing={1}>
                                      <Text
                                        fontSize="md"
                                        fontWeight="bold"
                                        color={textPrimary}
                                      >
                                        {formatCompactCurrency(
                                          project.totalConsumedCost,
                                          project.currency
                                        )}
                                      </Text>
                                      <Text fontSize="xs" color={textMuted}>
                                        of{" "}
                                        {formatCompactCurrency(
                                          project.totalBudgetedCost,
                                          project.currency
                                        )}
                                      </Text>
                                    </VStack>
                                  </Td>

                                  <Td>
                                    <HStack spacing={2}>
                                      <Tooltip
                                        label={`Schedule: ${project.health?.schedule}`}
                                      >
                                        <Badge
                                          size="sm"
                                          colorScheme={getHealthColor(
                                            project.health?.schedule
                                          )}
                                          px={2}
                                          py={1}
                                          borderRadius="md"
                                        >
                                          S
                                        </Badge>
                                      </Tooltip>
                                      <Tooltip
                                        label={`Budget: ${project.health?.budget}`}
                                      >
                                        <Badge
                                          size="sm"
                                          colorScheme={getHealthColor(
                                            project.health?.budget
                                          )}
                                          px={2}
                                          py={1}
                                          borderRadius="md"
                                        >
                                          B
                                        </Badge>
                                      </Tooltip>
                                      <Tooltip
                                        label={`Quality: ${project.health?.quality}`}
                                      >
                                        <Badge
                                          size="sm"
                                          colorScheme={getHealthColor(
                                            project.health?.quality
                                          )}
                                          px={2}
                                          py={1}
                                          borderRadius="md"
                                        >
                                          Q
                                        </Badge>
                                      </Tooltip>
                                      <Tooltip
                                        label={`Risk: ${project.health?.risk}`}
                                      >
                                        <Badge
                                          size="sm"
                                          colorScheme={getHealthColor(
                                            project.health?.risk
                                          )}
                                          px={2}
                                          py={1}
                                          borderRadius="md"
                                        >
                                          R
                                        </Badge>
                                      </Tooltip>
                                    </HStack>
                                  </Td>

                                  <Td>
                                    <HStack spacing={2}>
                                      <Tooltip label="View Project">
                                        <IconButton
                                          icon={<FiEye />}
                                          size="sm"
                                          variant="ghost"
                                          colorScheme="blue"
                                          onClick={() =>
                                            navigate(
                                              `/dashboard/company/projects/${project.id}`
                                            )
                                          }
                                          borderRadius="lg"
                                        />
                                      </Tooltip>
                                      <Menu>
                                        <MenuButton
                                          as={IconButton}
                                          icon={<FiMoreVertical />}
                                          size="sm"
                                          variant="ghost"
                                          borderRadius="lg"
                                        />
                                        <MenuList
                                          bg={bgPrimary}
                                          borderColor={borderColor}
                                          borderRadius="xl"
                                        >
                                          <MenuItem
                                            onClick={() =>
                                              navigate(
                                                `/dashboard/company/projects/${project.id}`
                                              )
                                            }
                                            borderRadius="lg"
                                            _hover={{ bg: hoverBg }}
                                          >
                                            <HStack spacing={2}>
                                              <Icon as={FiEye} />
                                              <Text>View Details</Text>
                                            </HStack>
                                          </MenuItem>
                                          <MenuItem
                                            onClick={() =>
                                              navigate(
                                                `/dashboard/company/projects/${project.id}/edit`
                                              )
                                            }
                                            borderRadius="lg"
                                            _hover={{ bg: hoverBg }}
                                          >
                                            <HStack spacing={2}>
                                              <Icon as={FiSettings} />
                                              <Text>Edit Project</Text>
                                            </HStack>
                                          </MenuItem>
                                        </MenuList>
                                      </Menu>
                                    </HStack>
                                  </Td>
                                </Tr>
                              );
                            })}
                          </Tbody>
                        </Table>
                      )}
                    </Box>
                  </Box>
                </VStack>
              </TabPanel>
            </TabPanels>
          </Tabs>
        </VStack>
      </Container>
    </Box>
  );
};

export default CompanyDashboard;
